import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { Plus, Edit, Trash2, Eye, Settings, Clock } from 'lucide-react';
import { getAllMenus, deleteMenu, menuTimingLabel } from '../../services/menuService';
import { confirm } from '../ui/confirm-dialog';

function MenusList({ restaurantId }) {
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Active / Inactive / All filter (client-side).
  const [statusFilter, setStatusFilter] = useState('all');
  const { toast } = useToast();

  useEffect(() => {
    loadMenus();
    // eslint-disable-next-line
  }, [restaurantId]);

  const loadMenus = async () => {
    if (!restaurantId) {
      console.warn('MenusList: restaurantId is not set');
      return;
    }
    setIsLoading(true);
    try {
      const data = await getAllMenus(restaurantId);
      setMenus(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('MenusList: Error loading menus:', error);
      setMenus([]);
      // Only show error toast if it's not a "no data" situation
      if (error.error && error.error !== 'Restaurant not found') {
        toast({
          title: 'Error',
          description: error.error || 'Failed to load menus',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (menuId) => {
    if (!(await confirm({ title: 'Delete menu?', description: `Are you sure you want to delete this menu?`, confirmText: 'Delete menu', tone: 'destructive' }))) return;

    try {
      await deleteMenu(restaurantId, menuId);
      toast({
        title: 'Success',
        description: 'Menu deleted successfully',
      });
      loadMenus();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete menu',
        variant: 'destructive',
      });
    }
  };

  return (
    <div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Restaurant Menus</CardTitle>
          </div>
          <Button onClick={() => navigate(`/restaurant/${restaurantId}/menus/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Menu
          </Button>
        </CardHeader>
        <CardContent>
          {!isLoading && menus.length > 0 && (
            <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-0.5 mb-4">
              {['all', 'active', 'inactive'].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  className={`px-3 py-1.5 text-sm rounded-md capitalize transition-colors ${
                    statusFilter === key
                      ? 'bg-background shadow-sm text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {key}
                </button>
              ))}
            </div>
          )}
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : menus.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No menus yet</p>
              <p className="text-sm">Create your first menu to get started</p>
              <Button 
                onClick={() => navigate(`/restaurant/${restaurantId}/menus/new`)}
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Menu
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {menus.filter((m) =>
                statusFilter === 'all'
                  ? true
                  : statusFilter === 'active'
                    ? m.is_active
                    : !m.is_active,
              ).map((menu) => {
                const timing = menuTimingLabel(menu);
                return (
                <Card key={menu.id} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{menu.name}</h3>
                        {menu.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {menu.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                          <span>Status: {menu.is_active ? 'Active' : 'Inactive'}</span>
                          <span>{menu.section_count || 0} section(s)</span>
                          {timing && (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {timing}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/restaurant/${restaurantId}/menus/${menu.id}`)}
                          title="View menu"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/restaurant/${restaurantId}/menus/${menu.id}/edit`)}
                          title="Edit menu"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(menu.id)}
                          title="Delete menu"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MenusList;
